import { CreateElement, VNode, VNodeData } from 'vue';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { getRange, setRange } from 'selection-ranges';

const VARIANTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'code',
  'inherit'
];

type Color =
  | 'default'
  | 'inherit'
  | 'error'
  | 'primary'
  | 'secondary'
  | 'white'
  | 'grey'
  | 'black'
  | 'textPrimary'
  | 'textSecondary';

type HeadlineMapping = {
  [key: string]: string;
  h1: string;
  h2: string;
  h3: string;
  h4: string;
  h5: string;
  subtitle1: string;
  subtitle2: string;
  body1: string;
  body2: string;
  button: string;
};

const defaultVariantMapping: HeadlineMapping = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  subtitle1: 'h5',
  subtitle2: 'h5',
  body1: 'p',
  body2: 'p',
  button: 'div'
};

type Align = 'inherit' | 'left' | 'center' | 'right' | 'justify';

type FontWeight = 'heavy' | 'medium' | 'regular' | 'light';

type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'button'
  | 'code';

@Component
export default class ClTypography extends Vue {
  /**
   * Set the text-align on the component.
   */
  @Prop({
    type: String,
    default: 'inherit',
    validator: (x: Align) =>
      ['inherit', 'left', 'center', 'right', 'justify'].includes(x)
  })
  readonly align!: Align;

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String as () => Color,
    default: 'default',
    validator: x =>
      [
        'default',
        'inherit',
        'error',
        'primary',
        'secondary',
        'white',
        'grey',
        'black',
        'textPrimary',
        'textSecondary'
      ].includes(x)
  })
  readonly color!: Color;

  /**
   * If set to true, the component is editable and can be used with v-model.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  readonly contenteditable!: boolean;

  /**
   * The component used for the root node.
   */
  @Prop(String)
  readonly component!: string;

  /**
   * If true, the text will have a bottom margin.
   */
  @Prop({
    default: false,
    type: Boolean
  })
  readonly gutterBottom!: boolean;

  /**
   * We are empirically mapping the variant property to a range of different
   * DOM element types. For instance, h1 to h6. If you wish to change that mapping, you can provide your own.
   * Alternatively, you can use the component property.
   */
  @Prop({
    default: () => defaultVariantMapping,
    type: Object as () => HeadlineMapping
  })
  readonly variantMapping!: HeadlineMapping;

  /**
   * If true, the text will not wrap. Instead it will truncate with an ellipsis.
   */
  @Prop({
    default: false,
    type: Boolean
  })
  readonly noWrap!: boolean;

  /**
   * If set to true, the component will be given a set pixel margin bottom.
   */
  @Prop({
    default: false,
    type: Boolean
  })
  readonly paragraph!: boolean;

  /**
   * The value of the component when `contenteditable` and `v-model` are inuse.
   */
  @Prop({
    default: '',
    type: [String, Number]
  })
  readonly value!: string | number;

  /**
   * The variant to use.
   */
  @Prop({
    type: String as () => Variant,
    default: 'body1',
    validator: variant => VARIANTS.includes(variant)
  })
  readonly variant!: Variant;

  @Prop({
    type: String as () => FontWeight,
    validator: x => ['heavy', 'medium', 'regular', 'light'].includes(x)
  })
  readonly fontWeight!: FontWeight;

  /**
   * Handle Input
   * in order to retain the cursor position on each sentence update
   * we need to get the range and reset it to what it was before the update
   */
  handleInput(event: Event) {
    this.$emit('input', (event.target as HTMLElement).innerHTML);
    const node = this.$refs.component;
    const range = getRange(node);
    if (range) {
      // when changing sentence data
      // we inadvertently reset the cursor position to 0
      // this will set the cursor position to
      // what it was before we rehydrate the sentence
      this.$nextTick(() => {
        setRange(node, range);
      });
    }
  }

  public render(h: CreateElement): VNode {
    const data: VNodeData = {
      ref: 'component',
      class: {
        'cl-typography': true,
        [`cl-typography--variant-${this.variant}`]: true,
        [`cl-typography--color-${this.color}`]: true,
        'cl-typography--nowrap': this.noWrap,
        'cl-typography--paragraph': this.paragraph,
        [`cl-typography--align-${this.align}`]: true,
        'cl-typography--gutterbottom': this.gutterBottom,
        'cl-typography--editable': this.contenteditable,
        [`cl-typography--font-weight-${this.fontWeight}`]: Boolean(
          this.fontWeight
        )
      },
      attrs: {
        contenteditable: this.contenteditable
          ? String(this.contenteditable)
          : undefined
      }
    };
    if (this.contenteditable) {
      data.on = {
        blur: (event: Event) => {
          this.$emit('blur', event);
        },
        focus: (event: Event) => {
          this.$emit('focus', event);
        },
        input: this.handleInput,
        keydown: (event: Event) => {
          this.$emit('keydown', event);
        },
        keyup: (event: Event) => {
          this.$emit('keyup', event);
        },
        paste: (event: Event) => {
          this.$emit('paste', event);
        }
      };
      data.domProps = {
        innerHTML: this.value
      };
    }
    const tag =
      this.component ||
      this.variantMapping[this.variant] ||
      defaultVariantMapping[this.variant] ||
      'span';
    return h(tag, data, this.$slots.default);
  }
}
